import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useUIStore } from '@jwp/ott-common/src/stores/UIStore';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useProfileStore } from '@jwp/ott-common/src/stores/ProfileStore';
import ProfileController from '@jwp/ott-common/src/controllers/ProfileController';
import { createURLFromLocation, modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { isTruthyCustomParamValue, unicodeToChar } from '@jwp/ott-common/src/utils/common';
import { ACCESS_MODEL } from '@jwp/ott-common/src/constants';
import useSearchQueryUpdater from '@jwp/ott-ui-react/src/hooks/useSearchQueryUpdater';
import { useProfiles, useSelectProfile } from '@jwp/ott-hooks-react/src/useProfiles';
import useOpaqueId from '@jwp/ott-hooks-react/src/useOpaqueId';
import { PATH_HOME, PATH_USER_PROFILES } from '@jwp/ott-common/src/paths';
import { determinePath } from '@jwp/ott-common/src/utils/urlFormatting';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import useAnalytics, { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { PATH_ACTIVATETV } from '@jwp/ott-common/src/paths';
import { useUIControls } from '@jwp/ott-hooks-react/src/useUIControls';
import ReactGA from 'react-ga4';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';
import { CombinedMenuItem, ExternalMenuItem, Menu } from '@jwp/ott-common/types/config';

import Header from '../../components/Header/Header';
import Sidebar from '../../components/Sidebar/Sidebar';
import MenuButton from '../../components/MenuButton/MenuButton';
import UserMenu from '../../components/UserMenu/UserMenu';
import Button from '../../components/Button/Button';
import Footer from '../../components/Footer/Footer';
import NavMenu from '../../components/NavMenu/NavMenu';

import { useTheme } from '../../../../../ThemeContext';
import { Auth } from 'aws-amplify';
import { getUserProfileData } from '../../../../common/src/helpers/AuthHelpers';

import styles from './Layout.module.scss';

import { AUTH_URL } from '@jwp/ott-web/src/config/clientEnvConfig';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import { useNavigationStack } from '@jwp/ott-common/src/stores/NavigationStackStore';
import { useContentStateStore } from '@jwp/ott-common/src/stores/ContentStateStore';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { universalStyles } = useTheme();
  const { t, i18n } = useTranslation('common');
  const { setAncestor } = useNavigationStack();
  const accountController = getModule(AccountController);
  const {
    isCognitoEnabled,
    isActivateTV,
    isAuthenticationEnabled,
    isShowLoginButton,
    isShowRegisterButton,
    isAnalyticsEnable,
    authentication_type,
    isSubscriptionEnabled,
    showSubscriptionPrompt,
    subscriptionPromptInterval,
  } = useAppConfig();
  const { redirectionSupportURL } = useUIControls();
  const [loading, setLoading] = useState(true);
  const deviceType = useGADeviceType();
  const user = useAccountStore((state) => state.user);
  const subscription = useAccountStore((state) => state.subscription);
  const accountLoading = useAccountStore((state) => state.loading);
  const isLoggedIn = !!user;
  const hasNoSubscription = isLoggedIn && !subscription;
  const userId = isLoggedIn ? user?.id : 'Guest user';

  const { config, accessModel, supportedLanguages, appConfig } = useConfigStore(
    ({ config, accessModel, supportedLanguages, appConfig }) => ({ config, accessModel, supportedLanguages, appConfig }),
    shallow,
  );

  const userMenuTitleId = useOpaqueId('usermenu-title');
  const favoritesEnabled = !!config.features?.favoritesList;
  const { menu, assets, siteName, description, features, styling, custom } = config;
  const metaDescription = description || t('default_description');
  const { footerText: configFooterText } = styling || {};
  const FooterText = () => {
    const year = new Date().getFullYear();
    return ` \u00a9 ${year} ${siteName} ${t('footer.copyright')}`;
  };

  const footerText = <>{configFooterText || unicodeToChar(FooterText())}</>;

  const profileController = getModule(ProfileController, false);

  const { searchPlaylist } = features || {};
  const hasAppContentSearch = isTruthyCustomParamValue(custom?.appContentSearch);
  const searchEnabled = !!searchPlaylist || hasAppContentSearch;

  const currentLanguage = useMemo(() => supportedLanguages.find(({ code }) => code === i18n.language), [i18n.language, supportedLanguages]);

  const {
    query: { data: { collection: profiles = [] } = {} },
    profilesEnabled,
  } = useProfiles();

  const selectProfile = useSelectProfile({
    onSuccess: () => navigate(PATH_HOME),
    onError: () => navigate(PATH_USER_PROFILES),
  });

  const { searchQuery, searchActive, userMenuOpen, languageMenuOpen } = useUIStore(
    ({ searchQuery, searchActive, userMenuOpen, languageMenuOpen }) => ({
      languageMenuOpen,
      searchQuery,
      searchActive,
      userMenuOpen,
    }),
    shallow,
  );

  const { updateSearchQuery, resetSearchQuery } = useSearchQueryUpdater();
  const { profile } = useProfileStore();

  const searchInputRef = useRef<HTMLInputElement>(null) as React.MutableRefObject<HTMLInputElement>;

  const [sideBarOpen, setSideBarOpen] = useState(false);
  const banner = assets.banner;
  const canLogin = accessModel !== ACCESS_MODEL.AVOD;

  useAnalytics();
  // useDeviceLogger();

  useEffect(() => {
    if (isLoggedIn && profilesEnabled && !profiles?.length) {
      profileController?.unpersistProfile();
    }
    // Trigger once on the initial page load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
    });
  };
  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    if (searchActive && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchActive]);

  const searchButtonClickHandler = () => {
    navigate(`/q/`);
    useUIStore.setState({
      searchActive: true,
      preSearchPage: `${location.pathname}${location.search || ''}`,
    });
  };

  const closeSearchButtonClickHandler = () => {
    resetSearchQuery();

    useUIStore.setState({
      searchActive: false,
    });
  };
  const loadData = async () => {
    try {
      const result = await accountController.loadUserData(isCognitoEnabled);

      if (result && isAnalyticsEnable) {
        sendGAEvent('login_success', {
          provider: authentication_type,
          method: authentication_type,
          userid_no: result?.user?.client_id,
          device_type: deviceType,
        });
      }
      if (result && result.userData && result.userData.ok === true) {
        await accountController.cognitoLogin(
          getUserProfileData(
            {
              idToken: result.user.signInUserSession.idToken.jwtToken,
              refreshToken: result.user.signInUserSession.refreshToken.token,
              emailId: result.user.signInUserSession.idToken.payload.email,
            },
            result.userData,
          ),
        );
        if (localStorage.getItem('otpVerification') === 'false') {
          navigate(isActivateTV ? PATH_ACTIVATETV : '/');
        }
      } else if (result && result.userData && result.userData.ok === false) {
        useAccountStore.setState({ loading: false });
        navigate(modalURLFromLocation(location, 'personal-details'));
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      const location = localStorage.getItem('redirectURL');
      if (location) {
        await navigate(location);
        localStorage.removeItem('redirectURL');
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (isCognitoEnabled) {
        await loadData();
      }
      setLoading(false);
    };

    fetchData();
  }, [isLoggedIn]);

  useEffect(() => {
    accountController.handleRefreshToken();
  }, []);

  const handleSignIn = async () => {
    try {
      await Auth.federatedSignIn();
      useAccountStore.setState({ loading: true });
    } catch (error) {
      console.error('Error signing in', error);
    }
  };

  const handleSignUp = () => {
    window.location.href = AUTH_URL;
  };

  const clearAuthStorage = () => {
    localStorage.removeItem('jwapp.auth');
    localStorage.removeItem('access_token');
    localStorage.removeItem('inplayer_token');
  };

  const loginButtonClickHandler = () => {
    clearAuthStorage();
    if (isCognitoEnabled) {
      handleSignIn();
    } else {
      navigate(modalURLFromLocation(location, 'login'));
    }
  };

  const signUpButtonClickHandler = () => {
    clearAuthStorage();
    if (isCognitoEnabled) {
      handleSignUp();
    } else {
      navigate(modalURLFromLocation(location, 'create-account'));
    }
  };

  const languageClickHandler = async (code: string) => {
    await i18n.changeLanguage(code);
  };

  // useCallbacks are used here to fix a bug in the Popover when using a Reactive onClose callback
  const openUserPanel = useCallback(() => useUIStore.setState({ userMenuOpen: true }), []);
  const closeUserPanel = useCallback(() => useUIStore.setState({ userMenuOpen: false }), []);
  const openLanguageMenu = useCallback(() => useUIStore.setState({ languageMenuOpen: true }), []);
  const closeLanguageMenu = useCallback(() => useUIStore.setState({ languageMenuOpen: false }), []);

  const renderUserActions = (sideBarOpen: boolean) => {
    if (!isAuthenticationEnabled) return null;

    return isLoggedIn ? (
      <section aria-labelledby={userMenuTitleId}>
        <UserMenu focusable={sideBarOpen} favoritesEnabled={favoritesEnabled} titleId={userMenuTitleId} showPaymentsItem sidebar />
      </section>
    ) : (
      <div className={styles.buttonContainer}>
        {isShowLoginButton && <Button tabIndex={sideBarOpen ? 0 : -1} onClick={loginButtonClickHandler} label={t('sign_in')} fullWidth />}
        {isShowRegisterButton && (
          <Button tabIndex={sideBarOpen ? 0 : -1} variant="contained" color="primary" onClick={signUpButtonClickHandler} label={t('sign_up')} fullWidth />
        )}
      </div>
    );
  };

  const containerProps = { inert: sideBarOpen ? '' : undefined }; // inert is not yet officially supported in react

  const availableCustomMenu = useMemo<ExternalMenuItem[]>(() => {
    if (!custom?.menu) return [];

    try {
      const parsed = typeof custom.menu === 'string' ? JSON.parse(custom.menu) : custom.menu;

      if (!Array.isArray(parsed)) return [];

      return parsed.filter((item): item is ExternalMenuItem => item?.platforms?.some((platform: string) => platform.toLowerCase() === 'web'));
    } catch (error) {
      console.error('Invalid custom.menu JSON', error);
      return [];
    }
  }, [custom]);

  const combinedMenu = useMemo<CombinedMenuItem[]>(() => {
    const cmsMenu = [...menu];
    const customMenu = [...availableCustomMenu];

    const totalLength = cmsMenu.length + customMenu.length;

    const result: CombinedMenuItem[] = new Array(totalLength);

    customMenu.forEach((item) => {
      if (typeof item.order === 'number') {
        const index = Math.min(item.order - 1, totalLength - 1);
        result[index] = item;
      }
    });

    let cmsIndex = 0;
    for (let i = 0; i < totalLength; i++) {
      if (!result[i] && cmsIndex < cmsMenu.length) {
        result[i] = cmsMenu[cmsIndex++];
      }
    }

    return result.filter(Boolean);
  }, [menu, availableCustomMenu]);

  const navItems = useMemo(
    () =>
      combinedMenu.map((item) => {
        if (item.external) {
          return {
            label: item.label,
            to: item.external?.url,
            target: item.external?.target,
            isExternal: true,
          };
        }

        const resolvedContentId = item.custom?.drmPlaylistId ?? item.contentId;

        return {
          label: item.label,
          contentId: item.contentId,
          to: determinePath({
            type: item.type as any,
            contentId: resolvedContentId,
            label: item.label,
          }),
          isExternal: false,
        };
      }),
    [combinedMenu],
  );

  const handleNavClick = (ancestor: string, item: Menu) => {
    useContentStateStore.setState({ click: item });
    setAncestor(ancestor);
    if (isAnalyticsEnable) {
      sendGAEvent('screen_view', {
        firebase_screen_class: item?.label,
        firebase_screen: item?.label,
        device_type: deviceType,
        userid_no: userId,
      });
    }
  };

  const SUBSCRIPTION_PROMPT_INTERVAL_MS = subscriptionPromptInterval * 60 * 1000;
  const SUBSCRIPTION_PROMPT_STORAGE_KEY = 'subscription_prompt_shown_at';
  const SUBSCRIPTION_PROMPT_FROM_REGISTRATION_KEY = 'subscription_prompt_from_registration';
  const locationRef = useRef(location);
  const navigateRef = useRef(navigate);
  locationRef.current = location;
  navigateRef.current = navigate;

  useEffect(() => {
    if (!showSubscriptionPrompt || !isSubscriptionEnabled) return;

    const currentLocation = locationRef.current;
    const isChooseOfferModalOpen = currentLocation.search.includes('u=choose-offer');

    if (!hasNoSubscription) {
      if (isChooseOfferModalOpen) {
        navigateRef.current(createURLFromLocation(currentLocation, { u: null, message: null }));
      }
      return;
    }

    if (accountLoading) return;

    const showChooseOfferIfNeeded = () => {
      const isFromFreshRegistration = sessionStorage.getItem(SUBSCRIPTION_PROMPT_FROM_REGISTRATION_KEY);
      if (isFromFreshRegistration) {
        sessionStorage.removeItem(SUBSCRIPTION_PROMPT_FROM_REGISTRATION_KEY);
        return;
      }

      const now = Date.now();
      const lastShownAt = parseInt(sessionStorage.getItem(SUBSCRIPTION_PROMPT_STORAGE_KEY) ?? '0', 10);
      if (lastShownAt && now - lastShownAt < SUBSCRIPTION_PROMPT_INTERVAL_MS) {
        return;
      }

      const loc = locationRef.current;
      const isOnPaymentsPage = loc.pathname.includes('payments');
      const isChooseOfferOpen = loc.search.includes('u=choose-offer');
      if (!isOnPaymentsPage && !isChooseOfferOpen) {
        navigateRef.current(modalURLFromLocation(loc, 'choose-offer'));
      }
    };

    showChooseOfferIfNeeded();
    const intervalId = setInterval(showChooseOfferIfNeeded, SUBSCRIPTION_PROMPT_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isSubscriptionEnabled, hasNoSubscription, accountLoading, SUBSCRIPTION_PROMPT_INTERVAL_MS, showSubscriptionPrompt, subscription]);

  if (loading) {
    return <LoadingOverlay />;
  }
  const hasOuterMenu = menu?.length > 0;
  const hasUserActions = !!renderUserActions(sideBarOpen);

  return (
    <div className={styles.layout}>
      <Helmet>
        <title>{siteName}</title>
        {/* <meta name="description" content={metaDescription} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:title" content={siteName} />
        <meta name="twitter:title" content={siteName} />
        <meta name="twitter:description" content={metaDescription} /> */}
      </Helmet>
      <div {...containerProps}>
        <Header
          onMenuButtonClick={() => setSideBarOpen(true)}
          logoSrc={appConfig?.branding?.logo_url}
          searchEnabled={searchEnabled}
          searchBarProps={{
            query: searchQuery,
            onQueryChange: (event) => updateSearchQuery(event.target.value),
            onClearButtonClick: () => updateSearchQuery(''),
            inputRef: searchInputRef,
          }}
          searchActive={searchActive}
          onSearchButtonClick={searchButtonClickHandler}
          onCloseSearchButtonClick={closeSearchButtonClickHandler}
          onLoginButtonClick={loginButtonClickHandler}
          onSignUpButtonClick={signUpButtonClickHandler}
          onLanguageClick={languageClickHandler}
          supportedLanguages={supportedLanguages}
          currentLanguage={currentLanguage}
          isLoggedIn={isLoggedIn}
          sideBarOpen={sideBarOpen}
          userMenuOpen={userMenuOpen}
          languageMenuOpen={languageMenuOpen}
          openUserPanel={openUserPanel}
          closeUserPanel={closeUserPanel}
          openLanguageMenu={openLanguageMenu}
          closeLanguageMenu={closeLanguageMenu}
          canLogin={canLogin}
          showPaymentsMenuItem={accessModel !== ACCESS_MODEL.AVOD}
          favoritesEnabled={favoritesEnabled}
          profilesData={{
            currentProfile: profile,
            profiles,
            profilesEnabled,
            selectProfile: ({ avatarUrl, id }) => selectProfile.mutate({ id, avatarUrl }),
            isSelectingProfile: selectProfile.isLoading,
          }}
        >
          <span onClick={() => handleNavClick('/', { label: t('home'), contentId: '' })}>
            <Button activeClassname={styles.headerButton} label={t('home')} to="/" variant="text" textStyle={universalStyles.navbarText} />
          </span>
          <NavMenu navItems={navItems} onNavClick={handleNavClick} />
          {/* 
          {navItems.map((item) => (
            <span key={item.to} onClick={() => handleNavClick(item.to, item)}>
              <Button activeClassname={styles.headerButton} label={item.label} to={item.to} variant="text" textStyle={universalStyles.navbarText} />
            </span>
          ))}
          */}
        </Header>
        <main id="content" className={styles.main} tabIndex={-1}>
          <Outlet />
        </main>
      </div>
      <Footer logoSrc={appConfig?.branding?.logo_url} text={footerText} menu={menu} />
      <Sidebar isOpen={sideBarOpen} onClose={() => setSideBarOpen(false)} showBorder={hasOuterMenu || hasUserActions}>
        <ul>
          <li onClick={() => handleNavClick('/', { label: t('home'), contentId: '' })}>
            <MenuButton label={t('home')} to="/" />
          </li>
          {navItems.map((item) => (
            <span key={item.to} onClick={() => handleNavClick(item.to, item)}>
              <MenuButton label={item.label} to={item.to} />
            </span>
          ))}
        </ul>
        {renderUserActions(sideBarOpen)}
      </Sidebar>
    </div>
  );
};

export default Layout;
